import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { usePermissionsHook } from "./usePermissionsHook";
import { useGroupsApi } from "./useGroupsHook";
import { emailInRoles } from "../constants/selectOptions/selectOptionsLoanRoles";

/**
 * EV-ONLY — who may EDIT the Underwriting loan note.
 *
 * Editing the Underwriting note is restricted to the "underwriting groups".
 * Access is granted when the user is an **admin** (admins always retain access)
 * OR matches ANY of three underwriting-group signals (union):
 *   1. the `underwriting` permission  → usePermissionsHook().isUnderwriter
 *   2. membership in an underwriting **org group** → GET /groups/for-user/{email}
 *      (a group whose name or relatedRoles reference underwriting / Pre-UW)
 *   3. the EV **loan-role rosters** — `underwriter` or `preUnderwriter`
 *      (Junior Underwriter) in selectOptionsLoanRoles.js
 *
 * Everyone else gets the Underwriting note **read-only**; the Processing note
 * stays editable for all. This restriction is **Eastview-only** — do NOT port
 * it to the sibling lendmarq-los repo (see CLAUDE.md).
 */
export const useCanEditUnderwritingNotes = () => {
  const { isAdmin, isUnderwriter } = usePermissionsHook();
  const email = useSelector((state) => state.user?.emailAddress);
  const { groupsForUser } = useGroupsApi();
  const [inUwGroup, setInUwGroup] = useState(false);

  // (3) roster match — synchronous.
  const rosterMatch = emailInRoles(email, ["underwriter", "preUnderwriter"]);

  // (2) org-group match — async. Skip the lookup entirely when a cheaper signal
  // already granted access, so we only pay for it for group-only members.
  useEffect(() => {
    let active = true;
    if (!email || isAdmin || isUnderwriter || rosterMatch) return undefined;
    groupsForUser(email)
      .then((groups) => {
        if (!active) return;
        const match = (groups || []).some((g) => {
          const roles = (g?.relatedRoles || []).map((r) =>
            String(r).toLowerCase(),
          );
          const name = String(g?.name || "").toLowerCase();
          return (
            roles.some((r) => r.includes("underwrit")) ||
            /underwrit|pre-?uw/.test(name)
          );
        });
        setInUwGroup(match);
      })
      .catch(() => {
        // for-user read may be gated; fall back to the sync signals above.
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, isAdmin, isUnderwriter, rosterMatch]);

  return isAdmin || isUnderwriter || rosterMatch || inUwGroup;
};

export default useCanEditUnderwritingNotes;
